import { typeValidator } from '@chilipiper/utils'
import { useControlledState } from '@react-stately/utils'
import type { Node } from '@react-types/shared'
import { AnimatePresence, motion } from 'framer-motion'
import * as R from 'remeda'
import React, {
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { HiddenSelect, ListKeyboardDelegate, useSelect, Key } from 'react-aria'
import { ListState, useSelectState } from 'react-stately'
import invariant from 'tiny-invariant'
import { Box, Flex, RootPortal, ScreenReaderOnly, Text } from '../../../core-components'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { Events } from '../../../types'
import { useElementSize } from '../../hooks'
import { SearchInput } from '../../input'
import { FocusableHandlerProps, PositioningProps } from '../../types'
import { SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import { SelectListBase } from '../select-list/SelectListBase'
import { isVisible } from '../select-list/useSelectListData'
import { useSelectListKeyboardDelegate } from '../select-list/useSelectListKeyboardDelegate'
import { BaseSelectPopover } from './BaseSelectPopover'
import { BaseSelectValue } from './BaseSelectValue'
import { CascadeItemSelectList } from './CascadeItemSelectList'
import { SelectItemDataProvider } from './SelectItemContext'
import { BaseSelectEndContent, StyledSelectTrigger } from './StyledSelectTrigger'
import { MIN_SELECT_LIST_WIDTH } from './constants'
import { createCascadePath } from './createCascadePath'
import { DynamicCascadeItem, DynamicItem, dynamicItemToItem } from './dynamicItemUtils'
import { ItemData } from './itemData.types'
import { useCascadeSelectState } from './useCascaseSelectState'
import { useCascadeListFocusRef } from './useSelectListsFocusManager'

type Variant = 'regular' | 'nested' | 'ghost'
type SelectionManager = ListState<unknown>['selectionManager']

export type BaseSelectProps = {
  INTERNAL_hideSelectList?: boolean
  INTERNAL_isOpen?: boolean
  'data-id'?: string
  'data-test-id'?: string
  defaultValue?: string
  errorMessage?: string
  eventsRef?: RefObject<Events<'open'> | null | undefined>
  hasLabelInside?: boolean
  hasUserProvidedSearch: boolean
  hasSearch?: boolean
  isDisabled?: boolean
  isRequired?: boolean
  isLoading?: boolean
  label: React.ReactNode
  listStateData: {
    readonly existingValues: ReadonlySet<string>
    readonly itemData: Record<Key, ItemData>
    readonly options: ReadonlyArray<DynamicItem>
  }
  name?: string
  onChange?: (value: string, path: string[]) => void
  onSearchChange?: (value: string) => void
  placeholder?: React.ReactNode
  search?: string
  selectedCascadeItem?: DynamicCascadeItem[]
  selectedText?: (
    selectedItem: Node<DynamicItem>,
    valuePath: readonly string[] | undefined,
    value?: string
  ) => ReactElement
  setSelectedCascadeItem?: React.Dispatch<React.SetStateAction<DynamicCascadeItem[] | undefined>>
  startIcon?: React.ComponentType
  useVirtualScroll?: boolean
  value?: string
  valuePath?: readonly string[]
  variant?: Variant
} & PositioningProps &
  FocusableHandlerProps

// eslint-disable-next-line complexity
const BaseSelectInternal = ({
  INTERNAL_hideSelectList,
  'data-id': dataId,
  'data-test-id': dataTestId,
  errorMessage,
  hasSearch,
  label,
  listStateData,
  hasLabelInside = false,
  name,
  placeholder = 'Select...',
  value,
  valuePath,
  variant = 'regular',
  isRequired,
  isDisabled,
  isLoading,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  onSearchChange,
  search = '',
  startIcon,
  selectedText,
  eventsRef,
  INTERNAL_isOpen,
  useVirtualScroll,
  setSelectedCascadeItem,
  selectedCascadeItem,
  hasUserProvidedSearch,
  ...positioningProps
}: BaseSelectProps) => {
  validatePositioningProps(positioningProps, 'BaseSelect')
  const {
    queueFocus,
    ref: setListRef,
    getCollection,
    observeCollection,
    getSelectionManager,
    getKeyboardDelegate,
    getFocusedPath,
  } = useCascadeListFocusRef()

  const rootValue = getRootValue(valuePath, value, defaultValue)
  const loadingNestedValuesState = useRef(R.doNothing())
  const {
    state,
    triggerRef,
    labelProps,
    descriptionProps,
    selectTriggerProps,
    valueProps,
    menuProps,
    errorMessageProps,
    keyboardDelegate,
  } = useSelectProps({
    listStateData,
    isDisabled,
    label,
    errorMessage,
    value: selectedCascadeItem?.length ? selectedCascadeItem[0].value : rootValue,
    defaultValue,
    onChange: onChange
      ? value => onChange(value as string /* we allow only string values in items */, [])
      : undefined,
    onBlur,
    onFocus,
    INTERNAL_isOpen,
    onOpenChange: isOpen => {
      loadingNestedValuesState.current()
      if (!isOpen) {
        onSearchChange?.('')
        setSelectedCascadeItem?.(undefined)
        state.selectionManager.setFocused(false)
        state.selectionManager.setFocusedKey(null)
      } else {
        let stop = false
        loadingNestedValuesState.current = () => {
          stop = true
        }
        const recheckSelectedCascadeItems = () => {
          if (state.selectedItem?.value?.itemType === 'cascade' && valuePath) {
            const selectedCascadeItems = createCascadePath(
              valuePath,
              state.selectedItem.value,
              getCollection
            )
            setSelectedCascadeItem?.(selectedCascadeItems)
            if (selectedCascadeItems.length < valuePath.length && !stop) {
              observeCollection(
                valuePath.slice(0, selectedCascadeItems.length).join('.'),
                recheckSelectedCascadeItems
              )
            }
          }
        }
        recheckSelectedCascadeItems()
        queueFocus(valuePath?.join('.') ?? '', value)
      }
    },
    onSelectCascadeItem: item => {
      setSelectedCascadeItem?.([item])
      queueFocus(item.value)
      onSearchChange?.('')
    },
  })
  useEffect(
    () =>
      setListRef(
        {
          keyboardDelegate,
          selectionManager: state.selectionManager,
          collection: state.collection,
        },
        ''
      ),
    [keyboardDelegate, state.selectionManager, state.collection, setListRef]
  )

  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    if (eventsRef) {
      eventsRef.current = {
        open: () => {
          setTimeout(() => stateRef.current.open(), 0)
        },
      }
      return () => (eventsRef.current = undefined)
    }
  }, [eventsRef])

  const fallbackName = useId()
  const nameWithFallback = name ?? fallbackName

  const { width: triggerWidth } = useElementSize(triggerRef)

  const handleSearchChange = (value: string) => {
    onSearchChange?.(value)
  }

  const rootValueNode = rootValue ? state.collection.getItem(rootValue) : undefined
  const masterListRightKey = useCallback(() => {
    if (selectedCascadeItem?.length) {
      queueFocus(selectedCascadeItem[0].value, selectedCascadeItem[1]?.value)
    }
  }, [selectedCascadeItem, queueFocus])

  const lastSelectListPath = selectedCascadeItem?.map(item => item.value).join('.') ?? ''

  const searchPlaceholder = useMemo(() => {
    const defaultPlaceholder = 'Search...'
    if (selectedCascadeItem) {
      return selectedCascadeItem
        .map(({ label }) => label)
        .concat(defaultPlaceholder)
        .join(' > ')
    }
    return defaultPlaceholder
  }, [selectedCascadeItem])

  const [leafEndContent, setLeafEndContent] = useState<ReactNode | undefined>()

  // TODO HiddenSelect is based on the root list state, in case of cascading select it will hold only the root value instead of real selected value, we should somehow include either the real value all the whole state (value + valuePath)
  return (
    <BaseSelectContainer
      label={label}
      isRequired={isRequired}
      labelProps={labelProps}
      descriptionProps={descriptionProps}
      hasLabelInside={hasLabelInside}
      positioningProps={positioningProps}
    >
      <HiddenSelect state={state} triggerRef={triggerRef} label={label} name={nameWithFallback} />
      <Box flexGrow={1} minWidth={0}>
        <StyledSelectTrigger
          data-id={dataId}
          data-test-id={dataTestId}
          isOpen={state.isOpen}
          variant={variant}
          hasError={!!errorMessage}
          h={getHeight(hasLabelInside, variant)}
          w='100%'
          pr={5}
          pl={5}
          borderRadius={variant !== 'nested' ? 10 : undefined}
          ariaProps={{
            ...selectTriggerProps,
            elementRef: triggerRef,
          }}
        >
          <Flex alignItems='center' justifyContent='space-between'>
            <BaseSelectValue
              startIcon={startIcon}
              valueProps={valueProps}
              hasLabelInside={hasLabelInside}
              isRequired={isRequired}
              labelProps={labelProps}
              label={label}
              rootValueNode={rootValueNode}
              selectedText={selectedText}
              valuePath={valuePath}
              value={value}
              placeholder={isLoading ? 'Loading...' : placeholder}
              onLeafEndContent={setLeafEndContent}
            />

            <BaseSelectEndContent
              errorMessage={errorMessage}
              variant={variant}
              endContent={
                state.selectedItem?.value?.itemType === 'item'
                  ? state.selectedItem?.value?.endContent
                  : leafEndContent
              }
            />
          </Flex>
        </StyledSelectTrigger>
        <RootPortal>
          {state.isOpen && !INTERNAL_hideSelectList && (
            <BaseSelectPopover
              triggerRef={triggerRef}
              state={state}
              triggerWidth={Math.max(triggerWidth, MIN_SELECT_LIST_WIDTH)}
              searchBar={
                hasSearch ? (
                  <SearchInput
                    variant='nested'
                    aria-label='Filter selection options'
                    INTERNAL_onKeyDown={e => {
                      switch (e.key) {
                        case 'Enter':
                          handleOnSerchEnter(
                            getSelectionManager(getFocusedPath() ?? '') ?? state.selectionManager
                          )
                          break
                        case 'ArrowUp': {
                          handleOnSearchSelectPrev(
                            getFocusedPath,
                            getSelectionManager,
                            getKeyboardDelegate,
                            queueFocus
                          )
                          e.preventDefault()
                          break
                        }
                        case 'ArrowDown': {
                          handleOnSearchSelectNext(
                            getFocusedPath,
                            getSelectionManager,
                            getKeyboardDelegate,
                            queueFocus
                          )
                          e.preventDefault()
                          break
                        }
                        case 'ArrowLeft': {
                          if (
                            handleOnSearchSelectLeft(
                              selectedCascadeItem,
                              value,
                              search,
                              getFocusedPath,
                              queueFocus
                            )
                          ) {
                            e.preventDefault()
                          }
                          break
                        }
                        case 'ArrowRight': {
                          if (
                            handleOnSearchSelectRight(
                              selectedCascadeItem,
                              search,
                              getFocusedPath,
                              getSelectionManager,
                              queueFocus
                            )
                          ) {
                            e.preventDefault()
                          }
                          break
                        }
                      }
                    }}
                    data-id={dataId}
                    onChange={value => {
                      const lastPanelCollection = getCollection(lastSelectListPath) ?? []
                      const findKey = () => {
                        for (const item of lastPanelCollection) {
                          const text = item.itemType === 'cascade' ? item.label : item.textValue
                          if (isVisible(value, text) && !item.isDisabled) {
                            return item.value
                          }
                        }
                      }
                      const key = findKey()
                      if (key) {
                        queueFocus(lastSelectListPath, key)
                      }
                      handleSearchChange(value)
                    }}
                    placeholder={searchPlaceholder}
                    value={search}
                    onClear={() => {
                      if (search === '') {
                        state.close()
                      }
                    }}
                  />
                ) : undefined
              }
            >
              <SelectItemDataProvider value={listStateData.itemData}>
                <Flex
                  minWidth={Math.max(triggerWidth, MIN_SELECT_LIST_WIDTH)}
                  overflow='hidden'
                  maxHeight='inherit'
                  bg='bg/dropdown'
                >
                  <SelectListBase
                    onClose={state.close}
                    keyboardDelegate={keyboardDelegate}
                    isLoading={isLoading}
                    {...menuProps}
                    variant='nested'
                    useVirtualScroll={useVirtualScroll}
                    useVirtualFocus={hasSearch}
                    state={state}
                    onFocusItem={key => queueFocus('', key)}
                    width={Math.max(triggerWidth, MIN_SELECT_LIST_WIDTH)}
                    onRightKey={masterListRightKey}
                    search={
                      selectedCascadeItem?.length || hasUserProvidedSearch ? undefined : search
                    }
                  />
                </Flex>
                <AnimatePresence initial={false}>
                  {setSelectedCascadeItem &&
                    selectedCascadeItem?.map((item, level, list) => {
                      const { currentPath, prevPath } = getItemPaths(list, level)
                      return (
                        <CascadeListAnimationWrapper
                          key={`${item.value}-${level}`}
                          itemValue={item.value}
                          value={list[level + 1]?.value ?? value}
                        >
                          <CascadeItemSelectList
                            value={list[level + 1]?.value ?? value}
                            onClose={state.close}
                            width={Math.max(triggerWidth, MIN_SELECT_LIST_WIDTH)}
                            onChange={value => {
                              onChange?.(
                                value,
                                selectedCascadeItem.slice(0, level + 1).map(item => item.value)
                              )
                              state.close()
                            }}
                            onSelectCascadeItem={cascadeItem => {
                              setSelectedCascadeItem((current = []) => [
                                ...current.slice(0, level + 1),
                                cascadeItem,
                              ])
                              queueFocus(`${currentPath}.${cascadeItem.value}`)
                              handleSearchChange('')
                            }}
                            onPrevList={() => queueFocus(prevPath, list[level].value)}
                            onFocusItem={key => queueFocus(currentPath, key)}
                            onNextList={() => {
                              if (list.length > level + 1) {
                                queueFocus(`${currentPath}.${list[level + 1].value}`)
                              }
                            }}
                            path={currentPath}
                            eventsRef={setListRef}
                            useVirtualScroll={useVirtualScroll}
                            useVirtualFocus={hasSearch}
                            search={
                              level === list.length - 1 && !hasUserProvidedSearch
                                ? search
                                : undefined
                            } // search only the last cascade panel
                          >
                            {item.children}
                          </CascadeItemSelectList>
                        </CascadeListAnimationWrapper>
                      )
                    })}
                </AnimatePresence>
              </SelectItemDataProvider>
            </BaseSelectPopover>
          )}
        </RootPortal>
        <BaseSelectError
          errorMessage={errorMessage}
          variant={variant}
          errorMessageProps={errorMessageProps}
        />
      </Box>
    </BaseSelectContainer>
  )
}

const CascadeListAnimationWrapper = ({
  children,
  value,
  itemValue,
}: {
  children: ReactNode
  itemValue: string
  value?: string
}) => {
  invariant(typeValidator(CascadeItemSelectList)(children))
  return (
    <motion.div
      key={itemValue}
      transition={{ duration: 0.2 }}
      initial={{ width: 0 }}
      animate={{ width: 'max-content' }}
      exit={{ width: 0 }}
      style={{
        position: 'relative',
        overflow: 'hidden',
        maxHeight: 'inherit',
        transformOrigin: 'right',
      }}
    >
      {/*
       * this is invisible Box if for the animation to work correctly,
       * we need to glue the right side of the list to the animating div,
       * we're doing that with `position: 'absolute'; right: 0`.
       * As the child is absolutely positioned we need to have something
       * that will keep the parent at the correct width for `width: max-content` to work.
       */}
      <Box
        w='max-content'
        boxShadow='border/cell-left'
        bg='bg/dropdown'
        maxHeight='inherit'
        overflow='hidden'
        style={{ visibility: 'hidden' }}
      >
        <CascadeItemSelectList
          onClose={R.doNothing()}
          onChange={R.doNothing()}
          onSelectCascadeItem={R.doNothing()}
          onPrevList={R.doNothing()}
          onNextList={R.doNothing()}
          onFocusItem={R.doNothing()}
          path=''
          value={value}
          width={children.props.width}
          useVirtualScroll={children.props.useVirtualScroll}
        >
          {children.props.children}
        </CascadeItemSelectList>
      </Box>
      <Box
        w='100%'
        right={0}
        top={0}
        bottom={0}
        boxShadow='border/cell-left'
        bg='bg/dropdown'
        overflow='hidden'
        maxHeight='inherit'
        position='absolute'
      >
        {children}
      </Box>
    </motion.div>
  )
}
export const BaseSelect = ({
  value,
  valuePath,
  defaultValue,
  onChange,
  ...props
}: BaseSelectProps) => {
  const inputValue = useMemo(() => (value ? { value, valuePath } : undefined), [value, valuePath])
  const inputDefaultValue = useMemo(
    () => ({ value: defaultValue, valuePath }),
    [defaultValue, valuePath]
  )
  const inputOnChange = useMemo(
    () =>
      onChange
        ? (data: { value: string; valuePath: string[] }) => onChange(data.value, data.valuePath)
        : undefined,
    [onChange]
  )
  const [stateValue, stateChange] = useControlledState(
    (() => {
      if (inputValue) {
        return inputValue
      }
      if (inputValue === undefined && onChange === undefined) {
        return undefined
      }
      return null
    })(),
    inputDefaultValue,
    inputOnChange
  )
  const remappedStateChange = useCallback(
    (value: string, valuePath: string[]) => stateChange({ value, valuePath }),
    [stateChange]
  )
  return (
    <BaseSelectInternal
      {...props}
      value={stateValue?.value}
      valuePath={stateValue?.valuePath}
      defaultValue={defaultValue}
      onChange={remappedStateChange}
    />
  )
}

const getHeight = (hasLabelInside: boolean, variant: Variant) => {
  if (hasLabelInside) {
    return 11
  }
  if (variant === 'nested') {
    return 9
  }
  return 8
}

const BaseSelectContainer = (props: {
  children: React.ReactNode
  descriptionProps: React.HTMLAttributes<unknown>
  hasLabelInside?: boolean
  isRequired?: boolean
  label: React.ReactNode
  labelProps: React.HTMLAttributes<unknown>
  positioningProps: PositioningProps
}) => {
  if (props.hasLabelInside) {
    return <Box {...props.positioningProps}>{props.children}</Box>
  }
  return (
    <SettingLabelContainer
      isRequired={props.isRequired}
      label={props.label}
      labelElement='div'
      labelProps={props.labelProps}
      descriptionProps={props.descriptionProps}
      {...props.positioningProps}
    >
      {props.children}
    </SettingLabelContainer>
  )
}
const BaseSelectError = (props: {
  errorMessage?: string
  errorMessageProps: React.DOMAttributes<unknown>
  variant: Variant
}) => {
  if (!props.errorMessage) {
    return null
  }
  if (props.variant === 'nested') {
    return <ScreenReaderOnly {...props.errorMessageProps}>{props.errorMessage}</ScreenReaderOnly>
  }

  return (
    <Text
      mt={2}
      color='fg/caution'
      display='block'
      textStyle='body-multiline-tight'
      {...props.errorMessageProps}
    >
      {props.errorMessage}
    </Text>
  )
}

const useSelectProps = ({
  isDisabled,
  label,
  listStateData,
  errorMessage,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  INTERNAL_isOpen,
  onOpenChange,
  onSelectCascadeItem,
}: {
  INTERNAL_isOpen?: boolean
  defaultValue?: string
  errorMessage?: string
  isDisabled?: boolean
  label: React.ReactNode
  listStateData: {
    readonly disabledKeys?: readonly string[]
    readonly existingValues: ReadonlySet<string>
    readonly options: ReadonlyArray<DynamicItem>
  }
  onBlur?: (event: React.FocusEvent) => void
  onChange: Parameters<typeof useSelectState>[0]['onSelectionChange']
  onFocus?: (event: React.FocusEvent) => void
  onOpenChange?: (isOpen: boolean) => void
  onSelectCascadeItem: (item: DynamicCascadeItem) => void
  value?: string
}) => {
  const ariaProps = {
    isDisabled,
    label,
    errorMessage,
    disabledKeys: listStateData.disabledKeys,
    children: dynamicItemToItem,
    items: listStateData.options,
    selectedKey: (() => {
      if (value === undefined && onChange === undefined) {
        return undefined
      }
      if (listStateData.existingValues.has(value as string)) {
        return value
      }
      return null
    })(),
    defaultSelectedKey: listStateData.existingValues.has(defaultValue as string)
      ? defaultValue
      : undefined,
    onSelectionChange: onChange,
    onFocus,
    onBlur,
    isOpen: INTERNAL_isOpen,
    onOpenChange,
    isInvalid: Boolean(errorMessage),
  }
  const state = useCascadeSelectState(useSelectState(ariaProps), onSelectCascadeItem)

  useEffect(() => {
    /**
     * This is a hack to trigger the intersection observer for items around the active item, which
     * seems like a sensible number. This is used to load the next/previous pages of items in the
     * select when navigating through collapsed select using keyboard.
     */
    const itemsAroundActive: Array<Node<DynamicItem>> = []

    const activeItemIndex = (() => {
      let index = 0
      for (const key of state.collection.getKeys()) {
        if (key === state.selectedItem?.key) {
          return index
        }
        index++
      }
    })()

    // Get 5 items before and after active item
    for (const element of state.collection) {
      const elementIndex = state.collection.getItem(element.key)?.index ?? -1
      if (
        activeItemIndex !== undefined &&
        elementIndex >= activeItemIndex - 5 &&
        elementIndex <= activeItemIndex + 5
      ) {
        itemsAroundActive.push(element)
      }
    }

    itemsAroundActive.forEach(item => {
      ;(item?.rendered as any)?.props?.children?.props?.onIntersection?.()
    })
  }, [state])

  const keyboardDelegate = useSelectListKeyboardDelegate(state.collection, state.disabledKeys)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const {
    labelProps,
    descriptionProps,
    triggerProps: selectTriggerProps,
    valueProps,
    menuProps,
    errorMessageProps,
  } = useSelect({ ...ariaProps, keyboardDelegate }, state, triggerRef)
  return {
    keyboardDelegate,
    state,
    triggerRef,
    labelProps,
    descriptionProps,
    selectTriggerProps,
    valueProps,
    menuProps,
    errorMessageProps,
  }
}

const getItemPaths = (list: readonly { readonly value: string }[], level: number) => {
  const prevPath = list
    .slice(0, level)
    .map(item => item.value)
    .join('.')
  const currentPath = list
    .slice(0, level + 1)
    .map(item => item.value)
    .join('.')
  return { currentPath, prevPath }
}

const getRootValue = (
  valuePath: readonly string[] | undefined,
  value: string | undefined,
  defaultValue: string | undefined
) => {
  if (valuePath?.length) {
    return valuePath[0]
  }
  if (value === undefined) {
    return defaultValue
  }
  return value
}

const handleOnSerchEnter = (selectionManager: SelectionManager) => {
  const focusedKey = selectionManager.focusedKey
  if (focusedKey !== null) {
    selectionManager.select(focusedKey)
  }
}
const handleOnSearchSelectNext = (
  getFocusedPath: () => string | undefined,
  getSelectionnManager: (path: string) => SelectionManager | undefined,
  getKeyboardDelegate: (path: string) => ListKeyboardDelegate<unknown> | undefined,
  queueFocus: (path: string, key?: Key) => void
) => {
  const focusedPath = getFocusedPath() ?? ''
  const selectionManager = getSelectionnManager(focusedPath)
  const keyboardDelegate = getKeyboardDelegate(focusedPath)
  const focusedKey = selectionManager?.focusedKey
  const key =
    focusedKey === null || focusedKey === undefined
      ? keyboardDelegate?.getFirstKey?.()
      : keyboardDelegate?.getKeyBelow?.(focusedKey)
  if (key) {
    queueFocus(focusedPath, String(key))
  }
}
const handleOnSearchSelectPrev = (
  getFocusedPath: () => string | undefined,
  getSelectionnManager: (path: string) => SelectionManager | undefined,
  getKeyboardDelegate: (path: string) => ListKeyboardDelegate<unknown> | undefined,
  queueFocus: (path: string, key?: Key) => void
) => {
  const focusedPath = getFocusedPath() ?? ''
  const selectionManager = getSelectionnManager(focusedPath)
  const keyboardDelegate = getKeyboardDelegate(focusedPath)
  const focusedKey = selectionManager?.focusedKey
  const key = focusedKey ? keyboardDelegate?.getKeyAbove?.(focusedKey) : undefined
  if (key) {
    queueFocus(focusedPath, key)
  }
}
const handleOnSearchSelectLeft = (
  selectedCascadeItem: readonly DynamicCascadeItem[] | undefined,
  value: string | undefined,
  searchValue: string | undefined,
  getFocusedPath: () => string | undefined,
  queueFocus: (path: string, key?: Key) => void
) => {
  if (!searchValue || searchValue.trim() === '') {
    return false
  }
  const focusedPath = getFocusedPath() ?? ''
  if (focusedPath !== '') {
    const path = focusedPath.split('.')
    const prevValue = selectedCascadeItem?.[path.length - 1]?.value ?? value

    queueFocus(path.slice(0, -1).join('.'), prevValue)
    return true
  }
  return false
}
const handleOnSearchSelectRight = (
  selectedCascadeItem: readonly DynamicCascadeItem[] | undefined,
  searchValue: string | undefined,
  getFocusedPath: () => string | undefined,
  getSelectionnManager: (path: string) => SelectionManager | undefined,
  queueFocus: (path: string, key?: Key) => void
) => {
  if (!searchValue || searchValue.trim() === '') {
    return false
  }
  const focusedPath = getFocusedPath() ?? ''
  const selectionManager = getSelectionnManager(focusedPath)
  const path = focusedPath === '' ? [] : focusedPath.split('.')
  const selectedKey = selectionManager?.firstSelectedKey
  if (selectedKey && selectedCascadeItem && selectedCascadeItem?.length > path.length) {
    queueFocus([...path, selectedKey].join('.'), selectedCascadeItem[path.length + 1]?.value)
    return true
  }
  return false
}
